import { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
  Search,
  Filter,
  Plus,
  X,
  Loader2,
  FolderKanban,
  Info,
} from "lucide-react";
import { cn } from "@/lib/utils";
import { formatDate, formatDateShort } from "@/lib/utils";
import { adminFetch } from "@/lib/api";
import { useFetch } from "@/hooks/use-fetch";
import { CurrencyInput } from "@/components/currency-input";
import { PageLoading } from "@/components/loading";
import { ErrorMessage } from "@/components/error-message";
import { Pagination } from "@/components/pagination";
import { SearchableSelect } from "@/components/searchable-select";
import { PROGRAM_LABELS, PROJECT_STATUS } from "@/lib/constants";

interface Project {
  id: number;
  title: string;
  client_name?: string;
  client_id: number;
  program_type: string;
  status: string;
  assigned_to_name?: string;
  current_stage_name?: string;
  created_at: string;
}

interface ProjectsResponse {
  data: Project[];
  pagination: {
    page: number;
    totalPages: number;
    total: number;
  };
}

interface Client {
  id: number;
  company_name: string;
}

interface ClientsResponse {
  data: Client[];
  pagination: { page: number; totalPages: number; total: number };
}

interface User {
  id: number;
  name: string;
}

interface UsersResponse {
  data: User[];
}

interface ActiveCallPeriod {
  id: number;
  title: string;
  institution: string;
  submission_deadline: string;
  eligible_cities: string;
}

interface ActiveCallPeriodsResponse {
  data: ActiveCallPeriod[];
}

interface Il {
  id: number;
  name: string;
  bolge: number;
}

interface Ilce {
  id: number;
  name: string;
}

const programOptions = [
  { value: "", label: "Tüm Programlar" },
  ...Object.entries(PROGRAM_LABELS).map(([value, label]) => ({
    value,
    label,
  })),
];

const statusOptions = [
  { value: "", label: "Tüm Durumlar" },
  ...Object.entries(PROJECT_STATUS).map(([value, { label }]) => ({
    value,
    label,
  })),
];

const initialForm = {
  client_id: "",
  program_type: "",
  title: "",
  description: "",
  city: "",
  district: "",
  nace_code: "",
  is_osb: false,
  investment_amount: "",
  consulting_fee: "",
  assigned_to: "",
  call_period_id: "",
};

export function ProjectsPage() {
  const [page, setPage] = useState(1);
  const [statusFilter, setStatusFilter] = useState("");
  const [programFilter, setProgramFilter] = useState("");
  const [search, setSearch] = useState("");
  const [showModal, setShowModal] = useState(false);
  const [form, setForm] = useState(initialForm);
  const [submitting, setSubmitting] = useState(false);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  // İl/İlçe state
  const [iller, setIller] = useState<Il[]>([]);
  const [ilceler, setIlceler] = useState<Ilce[]>([]);
  const [selectedIlId, setSelectedIlId] = useState<number>(0);

  // Auto-open modal when navigating with ?new=1
  useEffect(() => {
    if (searchParams.get("new") === "1") {
      const clientId = searchParams.get("client_id");
      if (clientId) {
        setForm((prev) => ({ ...prev, client_id: clientId }));
      }
      setShowModal(true);
    }
  }, [searchParams]);

  const queryParams = new URLSearchParams();
  queryParams.set("page", String(page));
  queryParams.set("limit", "20");
  if (statusFilter) queryParams.set("status", statusFilter);
  if (programFilter) queryParams.set("program_type", programFilter);
  if (search) queryParams.set("q", search);

  const { data, loading, error, refetch } = useFetch<ProjectsResponse>(
    `/projects?${queryParams.toString()}`,
    [page, statusFilter, programFilter, search]
  );

  const { data: clientsData } = useFetch<ClientsResponse>(
    "/clients?limit=200"
  );

  const { data: usersData } = useFetch<UsersResponse>("/users");

  const { data: callPeriodsData } = useFetch<ActiveCallPeriodsResponse>(
    "/call-periods/active"
  );

  // İlleri yükle
  useEffect(() => {
    if (!showModal) return;
    const API_BASE = import.meta.env.VITE_API_URL
      ? `${import.meta.env.VITE_API_URL}/api`
      : "/api";
    fetch(`${API_BASE}/il`)
      .then((r) => r.json())
      .then((json) => {
        const items = Array.isArray(json.data) ? json.data : [];
        setIller(items.sort((a: Il, b: Il) => a.name.localeCompare(b.name, "tr")));
      })
      .catch(() => {});
  }, [showModal]);

  // İlçeleri yükle
  useEffect(() => {
    if (!selectedIlId) {
      setIlceler([]);
      return;
    }
    const API_BASE = import.meta.env.VITE_API_URL
      ? `${import.meta.env.VITE_API_URL}/api`
      : "/api";
    fetch(`${API_BASE}/il/${selectedIlId}/ilce`)
      .then((r) => r.json())
      .then((json) => {
        const items = Array.isArray(json.data) ? json.data : [];
        setIlceler(items.sort((a: Ilce, b: Ilce) => a.name.localeCompare(b.name, "tr")));
      })
      .catch(() => {});
  }, [selectedIlId]);

  function handleCityChange(ilId: number) {
    setSelectedIlId(ilId);
    const il = iller.find((i) => i.id === ilId);
    setForm((prev) => ({ ...prev, city: il?.name || "", district: "" }));
  }

  function handleDistrictChange(ilceId: number) {
    const ilce = ilceler.find((i) => i.id === ilceId);
    setForm((prev) => ({ ...prev, district: ilce?.name || "" }));
  }

  function handleFormChange(field: string, value: string | boolean) {
    setForm((prev) => ({ ...prev, [field]: value }));
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!form.client_id || !form.program_type || !form.title.trim()) return;
    setSubmitting(true);
    try {
      const payload = {
        client_id: Number(form.client_id),
        program_type: form.program_type,
        title: form.title,
        description: form.description || undefined,
        city: form.city || undefined,
        nace_code: form.nace_code || undefined,
        is_osb: form.is_osb,
        investment_amount: form.investment_amount
          ? Number(form.investment_amount)
          : undefined,
        consulting_fee: form.consulting_fee
          ? Number(form.consulting_fee)
          : undefined,
        assigned_to: form.assigned_to ? Number(form.assigned_to) : undefined,
        call_period_id: form.call_period_id
          ? Number(form.call_period_id)
          : undefined,
      };
      await adminFetch("/projects", {
        method: "POST",
        body: JSON.stringify(payload),
      });
      setForm(initialForm);
      setShowModal(false);
      refetch();
    } catch {
      // Error shown via UI
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="space-y-4">
      {/* Filters */}
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <input
              type="text"
              placeholder="Proje ara..."
              value={search}
              onChange={(e) => {
                setSearch(e.target.value);
                setPage(1);
              }}
              className="h-9 w-full rounded-lg border border-border bg-secondary pl-9 pr-4 text-sm text-foreground placeholder:text-muted-foreground focus:border-accent focus:outline-none focus:ring-1 focus:ring-accent sm:w-60"
            />
          </div>
          <div className="flex items-center gap-2">
            <Filter className="h-4 w-4 text-muted-foreground" />
            <select
              value={statusFilter}
              onChange={(e) => {
                setStatusFilter(e.target.value);
                setPage(1);
              }}
              className="h-9 rounded-lg border border-border bg-secondary px-3 text-sm text-foreground focus:border-accent focus:outline-none focus:ring-1 focus:ring-accent"
            >
              {statusOptions.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
            <select
              value={programFilter}
              onChange={(e) => {
                setProgramFilter(e.target.value);
                setPage(1);
              }}
              className="h-9 rounded-lg border border-border bg-secondary px-3 text-sm text-foreground focus:border-accent focus:outline-none focus:ring-1 focus:ring-accent"
            >
              {programOptions.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
          </div>
        </div>
        <button
          onClick={() => setShowModal(true)}
          className="inline-flex items-center gap-2 rounded-lg bg-accent px-4 py-2 text-sm font-medium text-accent-foreground transition-colors hover:bg-accent/90"
        >
          <Plus className="h-4 w-4" />
          Yeni Proje
        </button>
      </div>

      {/* Table */}
      <div className="overflow-hidden rounded-xl border border-border bg-card">
        {loading ? (
          <PageLoading />
        ) : error ? (
          <ErrorMessage message={error} onRetry={refetch} className="m-4" />
        ) : !data || data.data.length === 0 ? (
          <div className="px-6 py-12 text-center text-sm text-muted-foreground">
            Henüz proje bulunmuyor.
          </div>
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-border">
                    <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-muted-foreground">
                      Proje Adı
                    </th>
                    <th className="hidden px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-muted-foreground md:table-cell">
                      Müşteri
                    </th>
                    <th className="hidden px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-muted-foreground lg:table-cell">
                      Program
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-muted-foreground">
                      Durum
                    </th>
                    <th className="hidden px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-muted-foreground xl:table-cell">
                      Danışman
                    </th>
                    <th className="hidden px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-muted-foreground lg:table-cell">
                      Aşama
                    </th>
                    <th className="hidden px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-muted-foreground sm:table-cell">
                      Tarih
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-border">
                  {data.data.map((project) => {
                    const statusInfo = PROJECT_STATUS[project.status] || {
                      label: project.status,
                      color: "text-muted-foreground",
                    };
                    return (
                      <tr
                        key={project.id}
                        onClick={() => navigate(`/projects/${project.id}`)}
                        className="cursor-pointer transition-colors hover:bg-secondary/50"
                      >
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-2">
                            <FolderKanban className="hidden h-4 w-4 shrink-0 text-muted-foreground sm:block" />
                            <span className="text-sm font-medium text-foreground">
                              {project.title}
                            </span>
                          </div>
                        </td>
                        <td className="hidden px-4 py-3 md:table-cell">
                          <span className="text-sm text-muted-foreground">
                            {project.client_name || "-"}
                          </span>
                        </td>
                        <td className="hidden px-4 py-3 lg:table-cell">
                          <span className="inline-flex items-center rounded-full border border-border px-2.5 py-0.5 text-xs font-medium text-muted-foreground">
                            {PROGRAM_LABELS[project.program_type] ||
                              project.program_type}
                          </span>
                        </td>
                        <td className="px-4 py-3">
                          <span
                            className={cn(
                              "inline-flex items-center rounded-full border px-2.5 py-0.5 text-xs font-medium",
                              statusInfo.color
                            )}
                          >
                            {statusInfo.label}
                          </span>
                        </td>
                        <td className="hidden px-4 py-3 xl:table-cell">
                          <span className="text-sm text-muted-foreground">
                            {project.assigned_to_name || "-"}
                          </span>
                        </td>
                        <td className="hidden px-4 py-3 lg:table-cell">
                          <span className="text-sm text-muted-foreground">
                            {project.current_stage_name || "-"}
                          </span>
                        </td>
                        <td className="hidden px-4 py-3 sm:table-cell">
                          <span className="text-sm text-muted-foreground">
                            {formatDate(project.created_at)}
                          </span>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            <Pagination
              page={data.pagination.page}
              totalPages={data.pagination.totalPages}
              onPageChange={setPage}
            />
          </>
        )}
      </div>

      {/* New Project Modal */}
      {showModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div
            className="absolute inset-0 bg-black/60"
            onClick={() => setShowModal(false)}
          />
          <div className="relative z-10 max-h-[90vh] w-full max-w-lg overflow-y-auto rounded-xl border border-border/60 bg-card p-6 shadow-2xl ring-1 ring-white/5">
            <div className="flex items-center justify-between">
              <h2 className="text-lg font-semibold text-foreground">
                Yeni Proje
              </h2>
              <button
                onClick={() => setShowModal(false)}
                className="rounded-lg p-1 text-muted-foreground transition-colors hover:bg-secondary hover:text-foreground"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
            <form onSubmit={handleSubmit} className="mt-4 space-y-4">
              <div>
                <div className="mb-1.5 flex items-center justify-between">
                  <label className="text-sm font-medium text-foreground">
                    Müşteri *
                  </label>
                  <a
                    href="/clients"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-xs text-accent hover:underline"
                  >
                    + Yeni müşteri ekle
                  </a>
                </div>
                <select
                  required
                  value={form.client_id}
                  onChange={(e) =>
                    handleFormChange("client_id", e.target.value)
                  }
                  className="h-9 w-full rounded-lg border border-border bg-secondary px-3 text-sm text-foreground focus:border-accent focus:outline-none focus:ring-1 focus:ring-accent"
                >
                  <option value="">Müşteri seçin...</option>
                  {clientsData?.data?.map((c) => (
                    <option key={c.id} value={c.id}>
                      {c.company_name}
                    </option>
                  ))}
                </select>
              </div>
              <div className="grid gap-4 sm:grid-cols-2">
                <div>
                  <label className="mb-1.5 block text-sm font-medium text-foreground">
                    Program Türü *
                  </label>
                  <select
                    required
                    value={form.program_type}
                    onChange={(e) =>
                      handleFormChange("program_type", e.target.value)
                    }
                    className="h-9 w-full rounded-lg border border-border bg-secondary px-3 text-sm text-foreground focus:border-accent focus:outline-none focus:ring-1 focus:ring-accent"
                  >
                    <option value="">Seçin...</option>
                    {Object.entries(PROGRAM_LABELS).map(([value, label]) => (
                      <option key={value} value={value}>
                        {label}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="mb-1.5 block text-sm font-medium text-foreground">
                    Danışman
                  </label>
                  <select
                    value={form.assigned_to}
                    onChange={(e) =>
                      handleFormChange("assigned_to", e.target.value)
                    }
                    className="h-9 w-full rounded-lg border border-border bg-secondary px-3 text-sm text-foreground focus:border-accent focus:outline-none focus:ring-1 focus:ring-accent"
                  >
                    <option value="">Seçin...</option>
                    {usersData?.data?.map((u) => (
                      <option key={u.id} value={u.id}>
                        {u.name}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
              <div>
                <label className="mb-1.5 block text-sm font-medium text-foreground">
                  Proje Başlığı *
                </label>
                <input
                  type="text"
                  required
                  value={form.title}
                  onChange={(e) => handleFormChange("title", e.target.value)}
                  className="h-9 w-full rounded-lg border border-border bg-secondary px-3 text-sm text-foreground placeholder:text-muted-foreground focus:border-accent focus:outline-none focus:ring-1 focus:ring-accent"
                />
              </div>
              <div>
                <label className="mb-1.5 block text-sm font-medium text-foreground">
                  Açıklama
                </label>
                <textarea
                  value={form.description}
                  onChange={(e) =>
                    handleFormChange("description", e.target.value)
                  }
                  rows={3}
                  className="w-full rounded-lg border border-border bg-secondary px-3 py-2 text-sm text-foreground focus:border-accent focus:outline-none focus:ring-1 focus:ring-accent"
                />
              </div>
              <div>
                <div className="mb-1.5 flex items-center justify-between">
                  <label className="text-sm font-medium text-foreground">
                    Çağrı Dönemi (opsiyonel)
                  </label>
                  <a
                    href="/call-periods"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-xs text-accent hover:underline"
                  >
                    + Yeni çağrı ekle
                  </a>
                </div>
                <select
                  value={form.call_period_id}
                  onChange={(e) =>
                    handleFormChange("call_period_id", e.target.value)
                  }
                  className="h-9 w-full rounded-lg border border-border bg-secondary px-3 text-sm text-foreground focus:border-accent focus:outline-none focus:ring-1 focus:ring-accent"
                >
                  <option value="">Çağrı seçin...</option>
                  {(callPeriodsData?.data ?? []).map((cp: ActiveCallPeriod) => (
                    <option key={cp.id} value={cp.id}>
                      {cp.title}
                    </option>
                  ))}
                </select>
                {(callPeriodsData?.data ?? []).length === 0 && (
                  <p className="mt-1 text-xs text-muted-foreground">
                    Henüz aktif çağrı dönemi bulunmuyor.
                    <a href="/call-periods" target="_blank" rel="noopener noreferrer" className="ml-1 text-accent hover:underline">
                      Çağrı ekleyin
                    </a>
                  </p>
                )}
                {form.call_period_id && (() => {
                  const selected = callPeriodsData?.data?.find(
                    (cp) => String(cp.id) === form.call_period_id
                  );
                  return (
                    <div className="mt-1.5 space-y-1">
                      {selected?.submission_deadline && (
                        <p className="text-xs text-muted-foreground">
                          Son teslim tarihi: {formatDateShort(selected.submission_deadline)}
                        </p>
                      )}
                      {selected?.eligible_cities && (
                        <div className="inline-flex items-center gap-1.5 rounded-md border border-info/30 bg-info/10 px-2 py-1 text-xs text-info">
                          <Info className="h-3 w-3" />
                          <span>Uygun iller: {selected.eligible_cities}</span>
                        </div>
                      )}
                    </div>
                  );
                })()}
              </div>
              <div className="grid gap-4 sm:grid-cols-2">
                <SearchableSelect
                  label="İl"
                  options={iller}
                  value={selectedIlId}
                  valueName={form.city}
                  onChange={(id) => handleCityChange(id)}
                  placeholder="İl seçin..."
                />
                <SearchableSelect
                  label="İlçe"
                  options={ilceler}
                  value={ilceler.find((i) => i.name === form.district)?.id || 0}
                  valueName={form.district}
                  onChange={(id) => {
                    const ilce = ilceler.find((i) => i.id === id);
                    if (ilce) handleDistrictChange(ilce.id);
                  }}
                  placeholder={selectedIlId ? "İlçe seçin..." : "Önce il seçin"}
                  disabled={!selectedIlId}
                />
                <div>
                  <label className="mb-1.5 block text-sm font-medium text-foreground">
                    NACE Kodu
                  </label>
                  <input
                    type="text"
                    value={form.nace_code}
                    onChange={(e) =>
                      handleFormChange("nace_code", e.target.value)
                    }
                    className="h-9 w-full rounded-lg border border-border bg-secondary px-3 text-sm text-foreground focus:border-accent focus:outline-none focus:ring-1 focus:ring-accent"
                  />
                </div>
                <CurrencyInput
                  label="Yatırım Tutarı"
                  value={form.investment_amount}
                  onChange={(v) => handleFormChange("investment_amount", v)}
                  placeholder="ör. 5.000.000"
                />
                <CurrencyInput
                  label="Danışmanlık Ücreti"
                  value={form.consulting_fee}
                  onChange={(v) => handleFormChange("consulting_fee", v)}
                  placeholder="ör. 150.000"
                />
              </div>
              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  id="is_osb"
                  checked={form.is_osb}
                  onChange={(e) =>
                    handleFormChange("is_osb", e.target.checked)
                  }
                  className="h-4 w-4 rounded border-border bg-secondary text-accent focus:ring-accent"
                />
                <label
                  htmlFor="is_osb"
                  className="text-sm font-medium text-foreground"
                >
                  OSB
                </label>
              </div>
              <div className="flex justify-end gap-3">
                <button
                  type="button"
                  onClick={() => setShowModal(false)}
                  className="rounded-lg border border-border px-4 py-2 text-sm font-medium text-muted-foreground transition-colors hover:bg-secondary hover:text-foreground"
                >
                  İptal
                </button>
                <button
                  type="submit"
                  disabled={submitting}
                  className={cn(
                    "inline-flex items-center gap-2 rounded-lg bg-accent px-4 py-2 text-sm font-medium text-accent-foreground transition-colors hover:bg-accent/90",
                    submitting && "cursor-not-allowed opacity-50"
                  )}
                >
                  {submitting && (
                    <Loader2 className="h-3.5 w-3.5 animate-spin" />
                  )}
                  Kaydet
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
